import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { signOut } from '@/libs/firebase/auth';
import { useUiStore } from '@/stores';
import type { GenericAbortSignal, InternalAxiosRequestConfig } from 'axios';

let scopeController = new AbortController();

let idTokenRevokedTeardown: Promise<void> | null = null;

function mergeAbortSignals(
  scope: GenericAbortSignal,
  incoming?: GenericAbortSignal,
): GenericAbortSignal {
  if (!incoming || incoming === scope) {
    return scope;
  }
  if (scope.aborted) {
    return scope;
  }
  if (incoming.aborted) {
    return incoming;
  }
  const merged = new AbortController();
  const propagate = (): void => {
    merged.abort();
  };
  scope.addEventListener?.('abort', propagate, { once: true });
  incoming?.addEventListener?.('abort', propagate, { once: true });
  return merged.signal;
}

export function attachFirebaseCloudFunctionsAbortScope(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  config.signal = mergeAbortSignals(scopeController.signal, config.signal);
  return config;
}

export function createFirebaseCloudFunctionsAbortInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) =>
      attachFirebaseCloudFunctionsAbortScope(config),
  };
}

/**
 * Cancels in-flight Cloud Function HTTP calls, signs the user out, then restores a fresh abort scope
 * for subsequent sessions. Concurrent callers share one teardown.
 */
export function enqueueFirebaseIdTokenRevokedTeardown(): Promise<void> {
  if (!idTokenRevokedTeardown) {
    idTokenRevokedTeardown = (async (): Promise<void> => {
      scopeController.abort();
      try {
        useUiStore.getState().hideGlobalLoading();
        await signOut();
      } finally {
        scopeController = new AbortController();
        idTokenRevokedTeardown = null;
        showAlert({
          title: STRINGS.auth.sessionTimeout.title,
          message: STRINGS.auth.sessionTimeout.message,
          buttons: [{ text: STRINGS.auth.sessionTimeout.signIn }],
        });
      }
    })();
  }
  return idTokenRevokedTeardown;
}
